import React from 'react';
import { motion } from 'framer-motion';
import { Sliders, Layout, Sparkles } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Sliders,
    title: 'Customize Your Experience',
    desc: 'Tell us your vision. We work with you to design a LED experience that matches the mood, aesthetic, and energy of your event perfectly.',
    accent: '#8B5CF6',
  },
  {
    number: '02',
    icon: Layout,
    title: 'We Design & Install',
    desc: 'Our team handles everything — from programming the light sequences to physical installation. Zero stress on your end. Total precision on ours.',
    accent: '#3B82F6',
  },
  {
    number: '03',
    icon: Sparkles,
    title: 'Your Event Becomes Unforgettable',
    desc: 'Guests arrive. The floor comes alive. Phones come out. And your event becomes the one everyone is still talking about months later.',
    accent: '#C084FC',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.18, duration: 0.7, ease: 'easeOut' },
  }),
};

export const HowItWorks = () => (
  <section
    id="how-it-works"
    data-testid="how-it-works-section"
    className="relative py-28 lg:py-36 px-6 overflow-hidden"
    style={{ background: '#0D0D0D' }}
  >
    {/* Background glow */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(139,92,246,0.06) 0%, transparent 70%)',
      }}
    />

    <div className="max-w-7xl mx-auto">
      <div className="mb-16 lg:mb-20">
        <motion.p
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeUp}
          className="text-xs font-manrope tracking-[0.3em] uppercase text-luma-purple mb-4"
        >
          Process
        </motion.p>
        <motion.h2
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeUp} custom={1}
          className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-white"
          data-testid="how-it-works-headline"
        >
          Simple process.
          <br />
          <span className="gradient-text">Extraordinary result.</span>
        </motion.h2>
      </div>

      {/* Steps */}
      <div className="relative">
        {/* Connecting line (desktop) */}
        <div className="hidden lg:block absolute top-16 left-0 right-0 h-px" style={{ background: 'rgba(139,92,246,0.12)' }} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.number}
                data-testid={`step-${step.number}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i + 1}
                className="relative flex flex-col"
              >
                {/* Step connector dot (desktop) */}
                <div className="hidden lg:flex items-center mb-12">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center relative z-10"
                    style={{
                      background: `${step.accent}20`,
                      border: `1px solid ${step.accent}50`,
                      boxShadow: `0 0 16px ${step.accent}30`,
                    }}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ background: step.accent }} />
                  </div>
                </div>

                {/* Number */}
                <span
                  className="font-outfit font-black text-5xl lg:text-6xl leading-none mb-6 lg:mb-8 block"
                  style={{ color: `${step.accent}25`, letterSpacing: '-0.04em' }}
                >
                  {step.number}
                </span>

                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{
                    background: `${step.accent}12`,
                    border: `1px solid ${step.accent}25`,
                  }}
                >
                  <Icon size={22} style={{ color: step.accent }} />
                </div>

                {/* Content */}
                <h3
                  className="font-outfit font-bold text-xl lg:text-2xl text-white mb-4 tracking-tight leading-tight"
                  data-testid={`step-title-${i}`}
                >
                  {step.title}
                </h3>
                <p className="font-manrope text-sm text-white/55 leading-relaxed">
                  {step.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default HowItWorks;
